/* =========================================================================== *\
 __      __.__     ___________           .__    ____ ________  ________.________
/  \    /  \__| ___\__    ___/___   ____ |  |__/_   /   __   \/   __   \   ____/
\   \/\/   /  |/    \|    |_/ __ \_/ ___\|  |  \|   \____    /\____    /____  \ 
 \        /|  |   |  \    |\  ___/\  \___|   Y  \   |  /    /    /    //       \
  \__/\  / |__|___|  /____| \___  >\___  >___|  /___| /____/    /____//______  /
       \/          \/           \/     \/     \/                             \/ 
\* =========================================================================== */

import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import PropTypes from 'prop-types';

// @mui material components
import Icon from '@mui/material/Icon';
import Backdrop from '@mui/material/Backdrop';
import CircularProgress from '@mui/material/CircularProgress';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableRow from '@mui/material/TableRow';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import IconButton from '@mui/material/IconButton';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import DialogActions from '@mui/material/DialogActions';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';

// Material Dashboard 2 PRO React components
import MDBox from 'components/MDBox';
import MDButton from 'components/MDButton';
import TabPanel from 'components/TabPanel/TabPanel';

// Material Dashboard 2 PRO React examples
import { useTranslation } from 'react-i18next';
import { APP_STATE } from 'services/Define';
import { FetchData } from 'services/Request';
import { useSelector } from 'react-redux';
import { TableHead } from '@mui/material';
import UploadExcel from './UploadExcel';

function ImportExcel({ callback, setButtonGroup }) {
  const mod = 'purchase_order_list';
  const app = useSelector((state) => state[APP_STATE]);
  const { t, i18n } = useTranslation();
  const { id } = useParams();
  const [record, setRecord] = useState([]);
  const [notify, setNotify] = useState(null);
  const [loading, setLoading] = useState(false);
  const [modalAdd, setModalAdd] = useState(false);
  const [tab, setTab] = useState(0);

  const showNotify = (msg, color) => {
    setNotify(
      <Snackbar
        open
        autoHideDuration={3000}
        onClose={() => setNotify(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={() => setNotify(null)} severity={color} variant='filled'>
          {msg}
        </Alert>
      </Snackbar>
    );
  };

  const upload = (filedata, file) => {
    const fileSize = file ? file.size : 0;
    if (fileSize > 1000000) {
      showNotify('ขนาดไฟล์ใหญ่จนเกินไป', 'error');
      return;
    }

    setLoading(true);

    FetchData('OPTIONS', `/${mod}/excel`, app.token, {
      id: id,
      filedata: filedata,
      filename: file ? file.name : '',
      filesize: file ? file.size : 0,
      filetype: file ? file.type : '',
    }).then((result) => {
      setLoading(false);

      if (result.status === 'ok') {
        setRecord(result.data);
        setModalAdd(true);
      } else {
        showNotify(t(`alert.saveerror`), 'error');
      }
    });
  };

  const select = () => {
    setModalAdd(false);

    callback(record[tab].data);
  };

  const setButton = () => {
    setButtonGroup(null);
  };

  const renderLoading = () => {
    return (
      <Backdrop sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }} open={loading}>
        <CircularProgress color='inherit' />
      </Backdrop>
    );
  };

  const renderList = () => {
    return (
      <MDBox>
        <MDBox>
          <Tabs
            value={tab}
            onChange={(e, v) => setTab(v)}
            aria-label='tabs-addedit'
            sx={{ overflowX: 'scroll' }}
          >
            {record.map((sheet, i) => (
              <Tab label={sheet.name} key={sheet.id} />
            ))}
          </Tabs>
        </MDBox>
        {record.map((sheet, i) => (
          <TabPanel value={tab} index={i} key={sheet.id}>
            <TableContainer sx={{ boxShadow: 0, minHeight: 500 }}>
              <Table aria-label='simple table'>
                <TableHead>
                  <TableRow>
                    <TableCell align='center'>No.</TableCell>
                    <TableCell align='left'>รหัสซื้อขาย</TableCell>
                    <TableCell align='right'>จำนวน</TableCell>
                    <TableCell align='right'>แถม</TableCell>
                    <TableCell align='right'>ราคาต่อหน่วย</TableCell>
                    <TableCell align='right'>ส่วนลดต่อหน่วย</TableCell>
                    <TableCell align='right'>น้ำหนักต่อหน่วย</TableCell>
                    <TableCell align='left'>เลขล็อต</TableCell>
                    <TableCell align='left'>เลขกำกับ</TableCell>
                    <TableCell align='left'>วัตถุดิบ/ผลผลิต</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sheet.data.map((row, j) => (
                    <TableRow key={row.A} hover>
                      <TableCell align='left'>{j + 1}</TableCell>
                      <TableCell align='left'>{row.A ?? ''}</TableCell>
                      <TableCell align='right'>{row.B ?? ''}</TableCell>
                      <TableCell align='right'>{row.C ?? ''}</TableCell>
                      <TableCell align='right'>{row.D ?? ''}</TableCell>
                      <TableCell align='right'>{row.E ?? ''}</TableCell>
                      <TableCell align='right'>{row.F ?? ''}</TableCell>
                      <TableCell align='left'>{row.G ?? ''}</TableCell>
                      <TableCell align='left'>{row.H ?? ''}</TableCell>
                      <TableCell align='left'>{row.I ?? ''}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </TabPanel>
        ))}
      </MDBox>
    );
  };

  const renderAdd = () => {
    return (
      <Dialog
        open={modalAdd}
        maxWidth='xxl'
        keepMounted
        aria-describedby='dialog-add'
        fullWidth
        onClose={() => setModalAdd(false)}
      >
        <DialogTitle>
          {t(`element.add`)}
          <IconButton
            aria-label='close'
            onClick={() => setModalAdd(false)}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <Icon>close</Icon>
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>{renderList()}</DialogContent>
        <DialogActions>
          <MDButton variant='gradient' color='info' onClick={select}>
            <Icon>add</Icon>&nbsp;{t(`element.add`)}
          </MDButton>
        </DialogActions>
      </Dialog>
    );
  };

  return (
    <>
      <UploadExcel uploadData={upload} setButton={setButton} />

      {renderAdd()}
      {renderLoading()}
      {notify}
    </>
  );
}

ImportExcel.propTypes = {
  callback: PropTypes.func.isRequired,
  setButtonGroup: PropTypes.func.isRequired,
};

export default ImportExcel;
